import { useTranslate } from '@refinedev/core';
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Typography,
  message,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ActivityDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ActivitySubject } from '@/components/system/ActivitySubject';
import { ActivityVerbTag, ExecutionSourceTag } from '@/components/system/tags';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface ActivityGeneralTabProps {
  record: ActivityDetailOut;
}

export const ActivityGeneralTab = ({ record }: ActivityGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();
  const f = (key: string) => t(`system.activities.fields.${key}`);

  const hasData = record.data && Object.keys(record.data).length > 0;
  const payload = hasData ? JSON.stringify(record.data, null, 2) : null;

  const eventItems: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'verb',
      label: f('verb'),
      children: orDash(record.verb && <ActivityVerbTag verb={record.verb} />),
    },
    {
      key: 'source',
      label: f('source'),
      children: orDash(
        record.source && <ExecutionSourceTag source={record.source} />
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'transaction_id',
      label: f('transaction_id'),
      children: <Text code>{record.transaction_id}</Text>,
    },
  ];

  const subjectItems: DescriptionsProps['items'] = [
    {
      key: 'object',
      label: f('object'),
      children: (
        <ActivitySubject type={record.object_type} id={record.object_id} />
      ),
    },
    {
      key: 'target',
      label: f('target'),
      children: (
        <ActivitySubject type={record.target_type} id={record.target_id} />
      ),
    },
  ];

  const clientItems: DescriptionsProps['items'] = [
    {
      key: 'actor',
      label: f('actor'),
      children: orDash(
        record.actor_id != null
          ? (record.actor_email ?? record.actor_id)
          : t('system.activities.empty_actor')
      ),
    },
    {
      key: 'ip_address',
      label: f('ip_address'),
      children: orDash(
        record.ip_address && (
          <Text code copyable>
            {record.ip_address}
          </Text>
        )
      ),
    },
    {
      key: 'user_agent',
      label: f('user_agent'),
      span: 3,
      // Up to 512 characters; wrapping keeps it from stretching the card.
      children: orDash(
        record.user_agent && (
          <Text style={{ overflowWrap: 'anywhere' }}>{record.user_agent}</Text>
        )
      ),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={eventItems}
        />
      </Card>

      <Card title={t('system.activities.sections.subject')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2 }}
          items={subjectItems}
        />
      </Card>

      <Card title={t('system.activities.sections.client')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={clientItems}
        />
      </Card>

      <Card
        title={t('system.activities.sections.data')}
        extra={
          payload && (
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(payload);
                message.success(t('common.copied'));
              }}
            >
              {t('common.copy')}
            </Button>
          )
        }
      >
        {payload ? (
          <pre
            style={{
              margin: 0,
              padding: token.padding,
              background: token.colorFillQuaternary,
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadius,
              fontSize: token.fontSizeSM,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            {payload}
          </pre>
        ) : (
          orDash(null)
        )}
      </Card>
    </Flex>
  );
};
